import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { pushSupported, subscribeToPush, unsubscribeFromPush } from "@/lib/push";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { User, Bell, BellOff, MessageCircle, CalendarCheck2, Smartphone, MapPin, Plus, X, Zap, Heart } from "lucide-react";
import NeighborhoodSelect from "@/components/NeighborhoodSelect";

const INTEREST_LABELS = {
  safety: "Safety alerts",
  marketplace: "Marketplace deals",
  events: "Events & meetups",
  recommend: "Recommendations",
  food: "Food & local eats",
  family: "Kids & family",
  utility: "Utility & roads",
  wellness: "Wellness & fitness",
};

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    neighborhood: user?.neighborhood || "",
    phone: user?.phone || "",
  });
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [prefBusy, setPrefBusy] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [hoodState, setHoodState] = useState(null);
  const [newHood, setNewHood] = useState("");
  const [hoodBusy, setHoodBusy] = useState(false);
  const canPush = pushSupported();

  useEffect(() => {
    api.get("/preferences").then(({ data }) => setPrefs(data)).catch(() => {});
    api.get("/neighborhoods").then(({ data }) => setHoodState(data)).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/auth/profile", form);
      await refresh();
      toast.success("Profile updated.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const setPref = async (key, value) => {
    setPrefBusy(true);
    try {
      const { data } = await api.patch("/preferences", { [key]: value });
      setPrefs(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPrefBusy(false); }
  };

  const togglePush = async (enable) => {
    setPushBusy(true);
    try {
      if (enable) {
        await subscribeToPush();
        setPrefs((p) => ({ ...(p || {}), push_enabled: true }));
        toast.success("Push notifications enabled");
      } else {
        await unsubscribeFromPush();
        setPrefs((p) => ({ ...(p || {}), push_enabled: false }));
        toast.success("Push notifications disabled");
      }
    } catch (err) {
      toast.error(err?.message || formatApiError(err));
    } finally { setPushBusy(false); }
  };

  const sendTestPush = async () => {
    setTestBusy(true);
    try {
      await api.post("/push/test");
      toast.success("Sent! Check your browser notifications.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setTestBusy(false); }
  };

  const joinHood = async (name) => {
    const clean = (name || "").trim();
    if (!clean) return;
    setHoodBusy(true);
    try {
      const { data } = await api.post("/neighborhoods/join", { name: clean });
      setHoodState((s) => ({ ...(s || {}), ...data }));
      setNewHood("");
      toast.success(`Joined ${clean}`);
      await refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setHoodBusy(false); }
  };

  const leaveHood = async (name) => {
    setHoodBusy(true);
    try {
      const { data } = await api.post("/neighborhoods/leave", { name });
      setHoodState((s) => ({ ...(s || {}), ...data }));
      toast.success(`Left ${name}`);
      await refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setHoodBusy(false); }
  };

  const switchHood = async (name) => {
    setHoodBusy(true);
    try {
      const { data } = await api.post("/neighborhoods/switch", { name });
      setHoodState((s) => ({ ...(s || {}), ...data }));
      toast.success(`Now viewing ${name}`);
      await refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setHoodBusy(false); }
  };

  return (
    <div data-testid="profile-page" className="mx-auto max-w-2xl px-4 py-10 sm:px-6 lg:px-8">
      <header className="mb-6">
        <div className="jirani-eyebrow">Your account</div>
        <h1 className="mt-2 flex items-center gap-3 text-4xl tracking-tight text-[#1A1A1A]" style={{ fontFamily: "Outfit", fontWeight: 800 }}>
          <User className="h-8 w-8 text-[#D95D39]" /> Profile
        </h1>
      </header>

      <div className="jirani-card rounded-2xl border border-black/5 bg-white p-6">
        <div className="flex items-center gap-4">
          <div className="grid h-16 w-16 place-items-center rounded-full bg-[#2E473B] text-xl font-semibold text-white">
            {(user?.name || "?").charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="text-lg font-semibold text-[#1A1A1A]">{user?.name}</div>
            <div className="text-sm text-[#4A4A4A]">{user?.email}</div>
            <div className="text-xs text-[#2E473B]/70">Signed in via {user?.provider || "password"}</div>
          </div>
        </div>

        <form onSubmit={save} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="p-name">Name</Label>
            <Input id="p-name" data-testid="profile-name-input"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 h-11 rounded-xl bg-white" />
          </div>
          <div>
            <Label htmlFor="p-hood">Neighbourhood</Label>
            <div className="mt-1">
              <NeighborhoodSelect
                id="p-hood"
                testId="profile-neighborhood-input"
                value={form.neighborhood}
                onChange={(v) => setForm({ ...form, neighborhood: v })}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="p-phone">Phone</Label>
            <Input id="p-phone" data-testid="profile-phone-input"
              value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="mt-1 h-11 rounded-xl bg-white" placeholder="+254 ..." />
          </div>
          <Button
            data-testid="profile-save-btn"
            type="submit" disabled={saving}
            className="jirani-pill rounded-full bg-[#D95D39] px-6 text-white hover:bg-[#c25333]"
          >
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </form>
      </div>

      <div data-testid="notification-prefs-card" className="jirani-card mt-6 rounded-2xl border border-black/5 bg-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="jirani-eyebrow">Preferences</div>
            <h2 className="mt-2 text-xl text-[#1A1A1A]" style={{ fontFamily: "Outfit", fontWeight: 700 }}>
              Notification settings
            </h2>
            <p className="mt-1 text-sm text-[#4A4A4A]">
              Choose which pings you'd like to hear. Muting only affects your own bell.
            </p>
          </div>
          <Bell className="h-6 w-6 text-[#D95D39]" />
        </div>

        <ul className="mt-5 divide-y divide-black/5">
          <li className="flex items-center justify-between gap-3 py-4">
            <div className="flex items-start gap-3">
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#F9F7F4] text-[#2E473B]">
                <MessageCircle className="h-4 w-4" />
              </div>
              <div>
                <div className="text-sm font-semibold text-[#1A1A1A]">Comments on your posts</div>
                <div className="text-xs text-[#4A4A4A]">Get a bell when a neighbor replies to something you shared.</div>
              </div>
            </div>
            <Switch
              data-testid="pref-mute-comments"
              disabled={prefBusy || !prefs}
              checked={!!prefs && !prefs.mute_comments}
              onCheckedChange={(v) => setPref("mute_comments", !v)}
              className="data-[state=checked]:bg-[#D95D39]"
            />
          </li>
          <li className="flex items-center justify-between gap-3 py-4">
            <div className="flex items-start gap-3">
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#F9F7F4] text-[#2E473B]">
                <CalendarCheck2 className="h-4 w-4" />
              </div>
              <div>
                <div className="text-sm font-semibold text-[#1A1A1A]">RSVPs to your events</div>
                <div className="text-xs text-[#4A4A4A]">Know when neighbors say they're joining an event you posted.</div>
              </div>
            </div>
            <Switch
              data-testid="pref-mute-rsvp"
              disabled={prefBusy || !prefs}
              checked={!!prefs && !prefs.mute_rsvp}
              onCheckedChange={(v) => setPref("mute_rsvp", !v)}
              className="data-[state=checked]:bg-[#D95D39]"
            />
          </li>
          <li className="flex items-center justify-between gap-3 py-4">
            <div className="flex items-start gap-3">
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#F9F7F4] text-[#2E473B]">
                <Smartphone className="h-4 w-4" />
              </div>
              <div>
                <div className="text-sm font-semibold text-[#1A1A1A]">Web push notifications</div>
                <div className="text-xs text-[#4A4A4A]">
                  {canPush
                    ? "A one-time browser prompt will ask permission — nothing until you say yes."
                    : "This browser doesn't support push. Try Chrome / Edge / Firefox on desktop or Android."}
                </div>
              </div>
            </div>
            <Button
              data-testid="pref-push-toggle"
              disabled={!canPush || pushBusy || !prefs}
              onClick={() => togglePush(!prefs?.push_enabled)}
              variant={prefs?.push_enabled ? "outline" : "default"}
              className={`jirani-pill rounded-full ${
                prefs?.push_enabled
                  ? "border-black/10 bg-white text-[#2E473B]"
                  : "bg-[#D95D39] text-white hover:bg-[#c25333]"
              }`}
            >
              {prefs?.push_enabled ? (
                <><BellOff className="mr-1.5 h-4 w-4" /> Disable push</>
              ) : (
                <><Bell className="mr-1.5 h-4 w-4" /> {pushBusy ? "Enabling..." : "Enable push"}</>
              )}
            </Button>
          </li>
          {prefs?.push_enabled && (
            <li className="flex items-center justify-between gap-3 py-3">
              <div className="text-xs text-[#4A4A4A]">
                Push is on. Send yourself a quick ping to confirm.
              </div>
              <Button
                data-testid="pref-push-test"
                onClick={sendTestPush}
                disabled={testBusy}
                variant="outline"
                className="jirani-pill rounded-full border-black/10 bg-white text-[#2E473B]"
              >
                <Zap className="mr-1.5 h-4 w-4 text-[#F4A261]" />
                {testBusy ? "Sending..." : "Send test ping"}
              </Button>
            </li>
          )}
        </ul>

        {prefs?.interests && prefs.interests.length > 0 && (
          <div data-testid="interests-summary" className="mt-6 rounded-2xl bg-[#F9F7F4] p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#2E473B]/70">
              <Heart className="h-3 w-3 text-[#D95D39]" /> Your interests
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {prefs.interests.map((k) => (
                <span
                  key={k}
                  className="rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-[#2E473B] shadow-sm"
                >
                  {INTEREST_LABELS[k] || k}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Neighborhoods */}
      <div data-testid="hoods-card" className="jirani-card mt-6 rounded-2xl border border-black/5 bg-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="jirani-eyebrow">Your neighbourhoods</div>
            <h2 className="mt-2 text-xl text-[#1A1A1A]" style={{ fontFamily: "Outfit", fontWeight: 700 }}>
              Neighborhoods you're part of
            </h2>
            <p className="mt-1 text-sm text-[#4A4A4A]">
              Join more than one mtaa — you can switch between them from the header any time.
            </p>
          </div>
          <MapPin className="h-6 w-6 text-[#D95D39]" />
        </div>

        <div data-testid="hoods-joined" className="mt-5 flex flex-wrap gap-2">
          {(hoodState?.neighborhoods || []).length === 0 && (
            <div className="text-xs text-[#4A4A4A]">You haven't joined any neighborhoods yet.</div>
          )}
          {(hoodState?.neighborhoods || []).map((h) => {
            const isActive = h === hoodState?.active_neighborhood;
            return (
              <span
                key={h}
                data-testid={`hood-chip-${h.replace(/\s+/g, "-")}`}
                className={`jirani-pill inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium ${
                  isActive
                    ? "border-transparent bg-[#2E473B] text-white"
                    : "border-black/10 bg-white text-[#2E473B]"
                }`}
              >
                <MapPin className="h-3 w-3" />
                <button
                  onClick={() => !isActive && switchHood(h)}
                  disabled={hoodBusy || isActive}
                  className={isActive ? "cursor-default" : "cursor-pointer"}
                  title={isActive ? "Current community" : `Switch to ${h}`}
                >
                  {h}{isActive && " · viewing"}
                </button>
                {(hoodState?.neighborhoods?.length || 0) > 1 && (
                  <button
                    data-testid={`hood-leave-${h.replace(/\s+/g, "-")}`}
                    onClick={() => leaveHood(h)}
                    disabled={hoodBusy}
                    className={`ml-1 rounded-full p-0.5 hover:bg-black/10 ${isActive ? "text-white/80" : "text-[#2E473B]/60"}`}
                    title={`Leave ${h}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            );
          })}
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); joinHood(newHood); }}
          className="mt-5 flex items-center gap-2"
        >
          <div className="flex-1">
            <NeighborhoodSelect
              testId="hood-input"
              value={newHood}
              onChange={(v) => setNewHood(v)}
              placeholder="Add a neighbourhood…"
              className="h-10"
            />
          </div>
          <Button
            data-testid="hood-add-btn"
            type="submit"
            disabled={hoodBusy || !newHood.trim()}
            className="jirani-pill rounded-full bg-[#D95D39] px-4 text-white hover:bg-[#c25333]"
          >
            <Plus className="mr-1 h-4 w-4" /> Join
          </Button>
        </form>

        {(hoodState?.popular || []).filter((h) => !(hoodState?.neighborhoods || []).includes(h)).length > 0 && (
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-widest text-[#2E473B]/70">Popular</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {(hoodState.popular || [])
                .filter((h) => !(hoodState.neighborhoods || []).includes(h))
                .slice(0, 10)
                .map((h) => (
                  <button
                    key={h}
                    data-testid={`hood-quick-${h.replace(/\s+/g, "-")}`}
                    onClick={() => joinHood(h)}
                    disabled={hoodBusy}
                    className="jirani-pill inline-flex items-center gap-1 rounded-full border border-dashed border-black/15 bg-[#F9F7F4] px-3 py-1 text-xs text-[#2E473B] hover:bg-white"
                  >
                    <Plus className="h-3 w-3" /> {h}
                  </button>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
